import clsx from 'clsx';
import { isEqual } from 'lodash';
import { PrimeReactContext } from 'primereact/api';
import { Calendar as PrimeReactCalendar } from 'primereact/calendar';
import type { FormEvent } from 'primereact/ts-helpers';
import { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import useTestTag from '../../../common/hooks/use-test-tag';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import EmptyFieldLabel from '../../labels/empty-field-label';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import OdinIcon, { OdinIconSize } from '../../odin-icon/odin-icon.component';
import type { FieldValidation } from './calendar-functions';
import {
  DEFAULT_LOCALE,
  normalizeDateFormat,
  parseDateTime,
  resolveDateBounds,
  resolveYearRange,
} from './calendar-functions';

export interface OdinCalendarProps extends DynamicFieldComponentProps<Date | Date[] | null | undefined> {
  showIcon?: boolean;
  iconPos?: 'left' | 'right';
  readOnlyInput?: boolean;
  showOnFocus?: boolean;
  showButtonBar?: boolean;
  appendTo?: 'self' | HTMLElement | undefined | null;
  selectionMode?: 'single' | 'multiple' | 'range';
  fieldValidations?: FieldValidation;
  /** PrimeReact date format string (e.g. 'dd/mm/yy'). When set, overrides the locale default. */
  dateFormat?: string;
}

function OdinCalendar({
  label,
  labelStyles = 'text-sm font-bold',
  value,
  onChange,
  required,
  readOnly,
  error,
  showIcon = true,
  iconPos = 'left',
  readOnlyInput = false,
  showOnFocus = true,
  showButtonBar = false,
  appendTo = 'self',
  selectionMode = 'single',
  disabled,
  placeholder,
  fieldValidations,
  dateFormat,
}: OdinCalendarProps) {
  const [valueState, setValueState] = useState(value);
  const primeReactContext = useContext(PrimeReactContext);

  const calendarRef = useRef<PrimeReactCalendar>(null);
  const inputId = useId();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only sync when value prop changes, check state to prevent redundant updates
  useEffect(() => {
    if (!isEqual(value, valueState)) {
      setValueState(value);
    }
  }, [value]);

  const tagCreator = useTestTag(label || '');
  const normalizedDateFormat = normalizeDateFormat(dateFormat);

  const handleParseDateTime = useCallback(
    (text: string) => {
      return parseDateTime(text, primeReactContext?.locale ?? DEFAULT_LOCALE, dateFormat);
    },
    [dateFormat, primeReactContext?.locale]
  );

  const handleChange = (event: FormEvent) => {
    setValueState(event.value);
    onChange?.(event.value);
  };

  const { minDate, maxDate } = useMemo(() => resolveDateBounds(fieldValidations), [fieldValidations]);
  const yearRange = useMemo(
    () => resolveYearRange({ minDate, maxDate, value: valueState }),
    [minDate, maxDate, valueState]
  );

  const handleShow = () => {
    calendarRef.current?.getInput()?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      // PrimeReact has no Enter handler on the input, so the browser fires a synthetic click on the
      // overlay's first focusable element (the "Choose Month" button), switching to month-picker view.
      // Blurring the input triggers PrimeReact's own blur/parse/onChange path — same as Tab.
      if (event.target === calendarRef.current?.getInput()) {
        event.preventDefault();
        calendarRef.current?.getInput()?.blur();
        calendarRef.current?.hide();
      }
    }
  };

  const decreaseYear = () => {
    const currentViewDate = calendarRef.current?.getViewDate() as Date;
    const newViewDate = new Date(currentViewDate);
    newViewDate.setFullYear(newViewDate.getFullYear() - 1);
    calendarRef.current?.updateViewDate(null, newViewDate);
  };

  const increaseYear = () => {
    const currentViewDate = calendarRef.current?.getViewDate() as Date;
    const newViewDate = new Date(currentViewDate);
    newViewDate.setFullYear(newViewDate.getFullYear() + 1);
    calendarRef.current?.updateViewDate(null, newViewDate);
  };

  const headerTemplate = () => (
    <>
      <button type="button" className="flex items-center justify-center p-datepicker-year-prev" onClick={decreaseYear}>
        <OdinIcon icon="ChevronsLeft" />
      </button>
      <button
        type="button"
        className="order-1 flex items-center justify-center p-datepicker-year-next"
        onClick={increaseYear}
      >
        <OdinIcon icon="ChevronsRight" />
      </button>
    </>
  );

  const icon = <OdinIcon size={OdinIconSize.ExtraSmall} icon="Calendar" />;
  const prevIcon = <OdinIcon icon="ChevronLeft" />;
  const nextIcon = <OdinIcon icon="ChevronRight" />;

  const isDisabled = !!disabled || !!readOnly;

  return (
    <div className="flex w-full flex-col" ref={tagCreator('calendar')}>
      {label && (
        <div className="flex">
          <FieldLabel label={label} name={inputId} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {readOnly && !valueState ? (
        <EmptyFieldLabel />
      ) : (
        <PrimeReactCalendar
          ref={calendarRef}
          inputId={inputId}
          className={clsx('odin py-0.5', { 'has-error': error, 'p-0': isDisabled })}
          panelClassName={clsx('min-w-[320px]')}
          appendTo={appendTo}
          showOnFocus={showOnFocus}
          locale={primeReactContext?.locale}
          dateFormat={normalizedDateFormat}
          value={valueState}
          onChange={handleChange}
          pt={{ root: { onKeyDown: handleKeyDown } }}
          disabled={isDisabled}
          icon={icon}
          showIcon={showIcon && !isDisabled}
          iconPos={iconPos}
          readOnlyInput={readOnlyInput}
          showButtonBar={showButtonBar}
          selectionMode={selectionMode}
          headerTemplate={headerTemplate}
          prevIcon={prevIcon}
          nextIcon={nextIcon}
          data-testid={`calendar-component-${label}`}
          yearNavigator
          yearRange={yearRange}
          monthNavigator
          parseDateTime={handleParseDateTime}
          onShow={handleShow}
          placeholder={placeholder}
          minDate={minDate}
          maxDate={maxDate}
          showMinMaxRange={Boolean(minDate || maxDate)}
          selectOtherMonths
        />
      )}
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
}

export default OdinCalendar;
